/**
 * @fileoverview Org portal — active students in a billing month (O1).
 *
 * An organisation is billed per active student, so the count that produces the
 * bill is shown to the customer BEFORE the bill arrives, with the names behind
 * it. A per-student price the payer cannot check is a per-student price they
 * have to take on trust, and the first surprise invoice is where that ends.
 *
 * The state badge is the honest part: "this month so far" is a running count
 * for a month still in progress, "settling" is a closed month inside its
 * late-confirmation window, and "final" is the frozen figure an invoice is
 * raised against.
 *
 * Layout: the month, the state and the four numbers stay PINNED above the
 * tabs, because they are what the admin came for and they are also the thing
 * every tab is an explanation of. The tabs below split the four explanations —
 * who was counted, which programs they were counted in, how the figure becomes
 * money, and the recording minutes the headcount buys — so the page answers one
 * question at a time instead of being a single long scroll of four.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import {
  Card,
  SectionHeader,
  StatCard,
  ListRow,
  Tag,
  Button,
  Icon,
  SegmentedControl,
} from "@/components/studio/primitives";

const PROGRAM_TYPE_LABELS = { private_lessons: "Private lessons", class: "Class", camp: "Camp" };

const filterClass =
  "rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-3 py-2 " +
  "font-sans text-[14px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-tertiary)] " +
  "focus:border-[var(--accent-yellow)]";

const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

const STATE_COPY = {
  live: { label: "This month so far", hint: "Still counting — the month has not ended." },
  settling: {
    label: "Settling",
    hint: "The month has ended. Lessons taught right at the end of it may still be confirmed for a few days.",
  },
  final: { label: "Final", hint: "Frozen. This is the figure an invoice for this month is raised against." },
};

/**
 * Sort options, in one table so the dropdown and the column headers can never
 * drift apart: the header buttons set the same `key`/`dir` pair the select
 * reads, and both render from this list.
 */
const SORTS = [
  { id: "name:asc", key: "name", dir: "asc", label: "Name A–Z" },
  { id: "name:desc", key: "name", dir: "desc", label: "Name Z–A" },
  { id: "instructor:asc", key: "instructor", dir: "asc", label: "Instructor" },
  { id: "programs:asc", key: "programs", dir: "asc", label: "Program" },
  { id: "review:desc", key: "review", dir: "desc", label: "Needs review first" },
];

/** What each sortable column sorts ON — a plain string, compared with locale rules. */
const SORT_VALUE = {
  name: (s) => (s.name || "").toLowerCase(),
  instructor: (s) => (s.instructor_name || "").toLowerCase(),
  programs: (s) => (s.programs || []).map((p) => p.title).join(", ").toLowerCase(),
  review: (s) => (s.unreviewed_import ? "1" : "0"),
};

/** The last 12 months, newest first, as YYYY-MM. */
function recentMonths(count = 12) {
  const out = [];
  const now = new Date();
  for (let i = 0; i < count; i += 1) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    out.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`);
  }
  return out;
}

function monthLabel(month) {
  const [y, m] = (month || "").split("-");
  if (!y || !m) return month;
  return new Date(Date.UTC(Number(y), Number(m) - 1, 1)).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** A tab's own empty state — says what belongs here, not just that it's empty. */
function Empty({ children, testId }) {
  return (
    <p data-testid={testId} className="px-2 py-10 text-center font-sans text-sm text-[var(--text-tertiary)]">
      {children}
    </p>
  );
}

/**
 * A clickable column header. The arrow only appears on the column actually in
 * force, so the header row stays quiet until you use it.
 */
function SortHeader({ label, sortKey, sort, onSort, className }) {
  const active = sort.key === sortKey;
  return (
    <th
      scope="col"
      aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
      className={["px-4 py-2.5 text-left font-normal", className].filter(Boolean).join(" ")}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        data-testid={`student-sort-${sortKey}`}
        className={[
          "inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-wide transition-colors duration-150",
          active ? "text-[var(--text-primary)]" : "text-[var(--text-tertiary)] hover:text-[var(--text-primary)]",
        ].join(" ")}
      >
        {label}
        {active ? <Icon name={sort.dir === "asc" ? "chevronUp" : "chevronDown"} size={13} strokeWidth={2} /> : null}
      </button>
    </th>
  );
}

export default function OrgUsagePage() {
  const months = useMemo(() => recentMonths(), []);
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [programFilter, setProgramFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [instructorFilter, setInstructorFilter] = useState("all");
  const [sort, setSort] = useState({ key: "name", dir: "asc" });
  const [month, setMonth] = useState(months[0]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);

  const load = useCallback(async (m) => {
    setLoading(true);
    try {
      const { data } = await orgApi.get(`/org/usage?month=${m}`);
      setUsage(data);
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't load usage"));
      setUsage(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(month);
  }, [load, month]);

  // Stripe sends the admin back here after a top-up. The minutes are credited
  // by the webhook, not by this redirect, so the toast says "on its way"
  // rather than asserting a balance this page has not re-read yet.
  useEffect(() => {
    const outcome = searchParams.get("topup");
    if (!outcome) return;
    if (outcome === "success") toast.success("Thanks — your minutes are on their way.");
    if (outcome === "cancelled") toast.info("No minutes were purchased.");
    searchParams.delete("topup");
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const buyMinutes = async () => {
    setBuying(true);
    try {
      const { data } = await orgApi.post("/org/topup");
      window.location.href = data.url;
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't start the purchase"));
      setBuying(false);
    }
  };

  const state = STATE_COPY[usage?.state] || STATE_COPY.live;
  const programs = Object.entries(usage?.by_program || {});
  // Its own memo, not an inline `|| []`: a fresh array literal every render
  // would make the filter/sort memo below recompute on every render, which is
  // the one thing on this page that is O(n log n).
  const allStudents = useMemo(() => usage?.students || [], [usage]);

  /* Filters narrow what is SHOWN, never what was counted: the total above is
     the billing figure and must not move because someone picked a program. */
  const shownStudents = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const matched = allStudents.filter((s) => {
      if (needle && !(s.name || "").toLowerCase().includes(needle)) return false;
      if (programFilter !== "all" && !(s.programs || []).some((p) => p.program_id === programFilter)) return false;
      if (typeFilter !== "all" && !(s.program_types || []).includes(typeFilter)) return false;
      if (instructorFilter !== "all" && !(s.instructors || [{ name: s.instructor_name }]).some((i) => i.name === instructorFilter)) return false;
      return true;
    });
    const value = SORT_VALUE[sort.key] || SORT_VALUE.name;
    // Name breaks every tie, so two students of the same instructor keep a
    // stable, readable order rather than whatever the payload happened to give.
    return matched.sort((a, b) => {
      const cmp = value(a).localeCompare(value(b));
      if (cmp !== 0) return sort.dir === "asc" ? cmp : -cmp;
      return SORT_VALUE.name(a).localeCompare(SORT_VALUE.name(b));
    });
  }, [allStudents, search, programFilter, typeFilter, instructorFilter, sort]);

  /* Clicking the column already in force reverses it; clicking another takes
     that column at its natural direction (names up, "needs review" first). */
  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: key === "review" ? "desc" : "asc" }));

  const filtered = shownStudents.length !== allStudents.length;

  /* The open tab lives in the URL, so a reload keeps it, a link can name it
     ("here's the costs breakdown"), and the browser's back button steps
     through tabs the way a reader expects. Students is the default and
     therefore writes no parameter at all. */
  const setTab = (id) => {
    const next = new URLSearchParams(searchParams);
    if (id === "students") next.delete("tab");
    else next.set("tab", id);
    setSearchParams(next, { replace: true });
  };

  const stats = [
    { testId: "usage-count", value: usage?.count ?? 0, label: "Active students", sub: monthLabel(month) },
    { value: programs.length, label: "Programs", sub: "with activity this month" },
    ...(usage?.bill
      ? [
          {
            testId: "usage-total",
            value: money(usage.bill.total_cents),
            label: "This month",
            sub: usage.state === "final" ? "final" : "so far",
          },
        ]
      : []),
    ...(usage?.stub_count > 0
      ? [
          {
            testId: "usage-stub-count",
            value: usage.stub_count,
            label: "Unreviewed imports",
            sub: "counted — confirm who they are",
          },
        ]
      : []),
  ];

  const TABS = [
    { id: "students", label: "Students", count: allStudents.length, testId: "usage-tab-students" },
    { id: "programs", label: "Programs", count: programs.length, testId: "usage-tab-programs" },
    ...(usage?.bill ? [{ id: "costs", label: "Costs", testId: "usage-tab-costs" }] : []),
    ...(usage?.pool ? [{ id: "minutes", label: "Minutes", testId: "usage-tab-minutes" }] : []),
  ];

  // A month without a pool has no Minutes tab; land the admin back on Students
  // rather than on a tab that no longer exists — which is also what happens to
  // a hand-typed ?tab= that names nothing.
  const tab = searchParams.get("tab") || "students";
  const activeTab = TABS.some((t) => t.id === tab) ? tab : "students";

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 md:px-8">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-x-6 gap-y-4">
        <div className="min-w-0">
          <h1 className="font-serif text-2xl font-semibold text-[var(--text-primary)]">Active students</h1>
          <p className="mt-1.5 max-w-xl font-sans text-sm text-[var(--text-tertiary)]">
            A student counts as active in a month once a lesson has been recorded for them in one of your programs.
          </p>
          {usage?.attribution_complete === false && <p role="status" className="mt-2 text-sm text-[var(--text-secondary)]">
            Some historical instructor and program details are unavailable. Totals are retained; filters may omit those rows.
          </p>}
        </div>

        {/* The month and its state travel together — the label on the figure is
            meaningless without the month it labels. */}
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor="usage-month" className="sr-only">
            Month
          </label>
          <select
            id="usage-month"
            data-testid="usage-month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-3 py-2 font-sans text-sm text-[var(--text-primary)] outline-none focus:border-[var(--accent-yellow)]"
          >
            {months.map((m) => (
              <option key={m} value={m}>
                {monthLabel(m)}
              </option>
            ))}
          </select>
          {usage ? (
            <Tag data-testid="usage-state" accent={usage.state === "final"}>
              {state.label}
            </Tag>
          ) : null}
        </div>
      </div>

      {loading ? (
        <Card>
          <p className="font-sans text-sm text-[var(--text-tertiary)]">Loading…</p>
        </Card>
      ) : !usage ? null : (
        <>
          {/* The columns follow the card COUNT, not a fixed four: the bill and
              the unreviewed-imports tile each come and go, and a fixed grid
              would leave a hole where the missing one reads as a card that
              failed to load. */}
          <div className={`mb-6 grid gap-4 sm:grid-cols-2 ${stats.length === 4 ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}>
            {stats.map((s) => (
              <StatCard key={s.label} data-testid={s.testId} value={s.value} label={s.label} sub={s.sub} />
            ))}
          </div>

          <p className="mb-6 font-sans text-[13px] text-[var(--text-tertiary)]">{state.hint}</p>

          <div className="mb-5 overflow-x-auto pb-1">
            <SegmentedControl testId="usage-tabs" value={activeTab} onChange={setTab} options={TABS} />
          </div>

          {activeTab === "students" ? (
            <Card padding={0}>
              <div className="px-5 pt-5">
                <SectionHeader>Who was counted</SectionHeader>
              </div>

              {allStudents.length === 0 ? (
                <Empty testId="usage-students-empty">
                  No lessons were recorded in your programs this month. A student appears here the first time one is.
                </Empty>
              ) : (
                <>
                  <div className="flex flex-wrap gap-2 px-5 pb-4">
                    <input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search students"
                      aria-label="Search students"
                      data-testid="student-search"
                      className={filterClass}
                    />
                    <select
                      value={programFilter}
                      onChange={(e) => setProgramFilter(e.target.value)}
                      aria-label="Filter by program"
                      data-testid="student-filter-program"
                      className={filterClass}
                    >
                      <option value="all">All programs</option>
                      {(usage.filters?.programs || []).map((p) => (
                        <option key={p.program_id} value={p.program_id}>
                          {p.title}
                        </option>
                      ))}
                    </select>
                    <select
                      value={typeFilter}
                      onChange={(e) => setTypeFilter(e.target.value)}
                      aria-label="Filter by program type"
                      data-testid="student-filter-type"
                      className={filterClass}
                    >
                      <option value="all">All types</option>
                      {(usage.filters?.program_types || []).map((t) => (
                        <option key={t} value={t}>
                          {PROGRAM_TYPE_LABELS[t] || t}
                        </option>
                      ))}
                    </select>
                    <select
                      value={instructorFilter}
                      onChange={(e) => setInstructorFilter(e.target.value)}
                      aria-label="Filter by instructor"
                      data-testid="student-filter-instructor"
                      className={filterClass}
                    >
                      <option value="all">All instructors</option>
                      {(usage.filters?.instructors || []).map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                    {/* The same sorts the column headers apply. It exists for
                        the narrow screens where the table scrolls sideways and
                        its headers are off the edge. */}
                    <select
                      value={`${sort.key}:${sort.dir}`}
                      onChange={(e) => {
                        const [key, dir] = e.target.value.split(":");
                        setSort({ key, dir });
                      }}
                      aria-label="Sort students"
                      data-testid="student-sort"
                      className={filterClass}
                    >
                      {SORTS.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {shownStudents.length === 0 ? (
                    <Empty testId="student-filter-empty">No students match those filters.</Empty>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[38rem] border-collapse text-left">
                        <thead>
                          <tr className="border-y border-[var(--studio-divider)]">
                            <SortHeader label="Student" sortKey="name" sort={sort} onSort={toggleSort} />
                            <SortHeader label="Instructor" sortKey="instructor" sort={sort} onSort={toggleSort} />
                            <SortHeader label="Programs" sortKey="programs" sort={sort} onSort={toggleSort} />
                            <SortHeader
                              label="Status"
                              sortKey="review"
                              sort={sort}
                              onSort={toggleSort}
                              className="w-[1%] whitespace-nowrap"
                            />
                          </tr>
                        </thead>
                        <tbody>
                          {shownStudents.map((s, i) => (
                            <tr
                              key={s.student_id}
                              data-testid={`usage-student-${s.student_id}`}
                              className={i < shownStudents.length - 1 ? "border-b border-[var(--studio-divider)]" : undefined}
                            >
                              <td className="px-4 py-3 font-sans text-[15px] text-[var(--text-primary)]">
                                {s.name || "Unnamed student"}
                              </td>
                              <td className="px-4 py-3 font-sans text-[14px] text-[var(--text-secondary)]">
                                {s.instructor_name || <span className="text-[var(--text-tertiary)]">—</span>}
                              </td>
                              <td className="px-4 py-3 font-sans text-[14px] text-[var(--text-secondary)]">
                                {(s.programs || []).map((p) => p.title).join(", ") || (
                                  <span className="text-[var(--text-tertiary)]">—</span>
                                )}
                              </td>
                              <td className="w-[1%] whitespace-nowrap px-4 py-3">
                                {s.unreviewed_import ? <Tag>Needs review</Tag> : null}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Say what is hidden — a filtered count that silently differs
                      from the total above would read as a billing discrepancy. */}
                  {filtered && (
                    <p className="px-5 py-4 font-sans text-[13px] text-[var(--text-tertiary)]">
                      Showing {shownStudents.length} of {allStudents.length}. Filters change what you see, not what was
                      counted.
                    </p>
                  )}
                </>
              )}
            </Card>
          ) : null}

          {activeTab === "programs" ? (
            <Card>
              <SectionHeader>By program</SectionHeader>
              {programs.length === 0 ? (
                <Empty testId="usage-programs-empty">
                  No program had activity this month. A program appears here once a lesson in it has notes.
                </Empty>
              ) : (
                <>
                  <div className="-mx-[18px]">
                    {programs.map(([pid, count], i) => (
                      <ListRow
                        key={pid}
                        title={usage.program_titles?.[pid] || pid}
                        meta={`${count} student${count === 1 ? "" : "s"}`}
                        borderBottom={i < programs.length - 1}
                      />
                    ))}
                  </div>
                  <p className="mt-4 font-sans text-[13px] text-[var(--text-tertiary)]">
                    A student taught in two programs is counted once in the total above, and once in each program here.
                  </p>
                </>
              )}
            </Card>
          ) : null}

          {activeTab === "costs" && usage.bill ? (
            <Card>
              <SectionHeader>How that adds up</SectionHeader>
              <div className="-mx-[18px]">
                <ListRow
                  title="Base subscription"
                  subtitle={`Includes your first ${usage.bill.included_students} active students`}
                  meta={money(usage.bill.base_cents)}
                  borderBottom
                />
                <ListRow
                  title={`Students ${usage.bill.included_students + 1}–${usage.bill.band_break}`}
                  subtitle={`${usage.bill.band1_students} × ${money(usage.bill.band1_rate_cents)}`}
                  meta={money(usage.bill.band1_cents)}
                  borderBottom
                />
                <ListRow
                  title={`Students ${usage.bill.band_break + 1} and above`}
                  subtitle={`${usage.bill.band2_students} × ${money(usage.bill.band2_rate_cents)}`}
                  meta={money(usage.bill.band2_cents)}
                  borderBottom={usage.bill.add_ons.length > 0}
                />
                {usage.bill.add_ons.length > 0 ? (
                  <ListRow
                    title="Add-ons"
                    subtitle={usage.bill.add_ons.join(", ")}
                    meta={money(usage.bill.add_ons_cents)}
                  />
                ) : null}
              </div>
              <p className="mt-4 font-sans text-[13px] text-[var(--text-tertiary)]">
                The reduced rate applies only to students above {usage.bill.band_break}, not to all of them. The base
                bills every month; per-student charges bill in arrears for the month just taught.
              </p>
            </Card>
          ) : null}

          {/* Only for the month in progress: a past month's pool is spent and
              unbuyable, so offering minutes there would be a purchase that
              changes nothing. */}
          {activeTab === "minutes" && usage.pool ? (
            <Card>
              <SectionHeader>AI recording minutes</SectionHeader>
              <p className="font-sans text-sm text-[var(--text-secondary)]">
                Your plan includes {usage.pool.hours_per_student} hours of AI recording per active student each month,
                shared across everyone at your organization. If you run out you can buy more, and they never expire.
              </p>
              <p className="mt-2 font-mono text-[13px] text-[var(--text-tertiary)]">
                {Math.round(usage.pool.minutes_used)} of {Math.round(usage.pool.minutes_limit)} minutes used
                {usage.pool.credit_minutes > 0
                  ? ` · ${Math.round(usage.pool.credit_minutes)} extra in reserve`
                  : ""}
              </p>
              <div className="mt-4">
                <Button onPress={buyMinutes} disabled={buying} data-testid="usage-topup">
                  {buying ? "Opening checkout…" : "Buy minutes"}
                </Button>
              </div>
            </Card>
          ) : null}

          <div className="mt-6">
            <Button variant="ghost" onPress={() => load(month)} data-testid="usage-refresh">
              Refresh
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
